import {
  CAlert,
  CButton,
  CButtonGroup,
  CCol,
  CContainer,
  CForm,
  CFormGroup,
  CInput,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CRow,
  CTextarea,
} from "@coreui/react";
import React, { useEffect, useRef, useState } from "react";
import {
  useCreateBinary,
  useCreateModelVersion,
  useGetModelVersion,
  usePatchModelVersion,
} from "../models/ModelService";

import CIcon from "@coreui/icons-react";
import PropTypes from "prop-types";
import { closeAndScrollToId } from "./FormUtils";
import { isObjEmpty } from "src/reusable/Functions";
import { useHistory } from "react-router-dom";

const validateBinary = (filename) => {
  let errors = [];
  if (!filename || filename.name === "") {
    errors.push("File must be provided");
  }
  return errors;
};

const validateConfig = (config) => {
  let errors = [];
  if (config.length !== 0) {
    try {
      JSON.parse(config);
    } catch (error) {
      errors.push("Config must be valid JSON, error when parsing:  " + error);
    }
  }
  return errors;
};

const validateResourceLimits = (cpuLimit, memoryLimit) => {
  let errors = [];
  if (cpuLimit !== "" && cpuLimit !== null) {
    const cpuValue = parseFloat(cpuLimit);
    if (Number.isNaN(cpuValue)) {
      errors.push("CPU Limit must be a valid number");
    } else if (cpuValue <= 0) {
      errors.push("CPU Limit must be a positive number");
    }
  }
  if (memoryLimit !== "" && memoryLimit !== null) {
    const memoryValue = parseInt(memoryLimit, 10);
    if (Number.isNaN(memoryValue)) {
      errors.push("Memory Limit must be a valid number");
    } else if (memoryValue <= 0) {
      errors.push("Memory Limit must be a positive number");
    }
  }
  return errors;
};

// Build final config object by merging base config JSON with cpu_limit and memory_limit
const buildConfigWithLimits = (configJson, cpuLimit, memoryLimit) => {
  let finalConfig = configJson.length > 0 ? JSON.parse(configJson) : {};
  const cpuValue = parseFloat(cpuLimit);
  if (cpuLimit !== "" && cpuLimit !== null && !Number.isNaN(cpuValue)) {
    finalConfig.cpu_limit = cpuValue;
  }
  const memoryValue = parseInt(memoryLimit, 10);
  if (
    memoryLimit !== "" &&
    memoryLimit !== null &&
    !Number.isNaN(memoryValue)
  ) {
    finalConfig.memory_limit = memoryValue;
  }
  return finalConfig;
};
// TODO: Fix eslint line complexity

export const CreateModelVersionModal = (props) => {
  const defaultModelBaseImage = "modelbase:latest";
  const history = useHistory();
  const [file, setFile] = useState("");
  const [config, setConfig] = useState("");
  const inputFile = useRef(null);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [modelBaseImage, setModelBaseImage] = useState(defaultModelBaseImage);
  const [cpuLimit, setCpuLimit] = useState("");
  const [memoryLimit, setMemoryLimit] = useState("");
  // Operation status:
  const OPERATION_WORKFLOW = {
    IDLE: 0,
    UPLOADING_FILE: 1,
    FILE_UPLOADED: 2,
    CHECKING_FILE_EXISTS: 3,
    FILE_EXISTS: 4,
    FILE_EXISTS_CANCEL: 5,
    FILE_DOESNT_EXIST: 6,
    CREATING_MODEL_VERSION: 7,
    MODEL_VERSION_CREATED: 8,
    COMPLETE: 9,
  };
  const [operationStatus, setOperationStatus] = React.useState(
    OPERATION_WORKFLOW.IDLE,
  );
  const {
    mutate: createBinary,
    isError: isCreateBinaryError,
    isSuccess: isCreateBinarySuccess,
    error: createBinaryError,
    data: newBinary,
    reset: resetCreateBinary,
  } = useCreateBinary();

  const {
    mutate: createModelVersion,
    isError: isCreateModelVersionError,
    isSuccess: isCreateModelVersionSuccess,
    error: createModelVersionError,
    data: newModelVersion,
    reset: resetModelVersion,
  } = useCreateModelVersion();

  const resetAfterClose = () => {
    setFile(null);
    setModelBaseImage("");
    setConfig("");
    setCpuLimit("");
    setMemoryLimit("");
    // You have to do this with file inputs for some reason to reset them :/
    // Guard against a null ref: the deferred close (closeAndScrollToId) can fire
    // after the modal — and this file input — has unmounted (FRONTEND-WE).
    if (inputFile.current) inputFile.current.value = "";
    setErrorMessage(null);
    setOperationStatus(OPERATION_WORKFLOW.IDLE);
    resetCreateBinary();
    resetModelVersion();
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  // First step in the workflow, upload the file
  const onCreateBinarySubmit = () => {
    let errors = validateBinary(file);
    errors = errors.concat(validateConfig(config));
    errors = errors.concat(validateResourceLimits(cpuLimit, memoryLimit));
    if (errors.length === 0) {
      setErrorMessage(null);
      setOperationStatus(OPERATION_WORKFLOW.UPLOADING_FILE);
      createBinary(file);
    } else {
      setErrorMessage(errors.map((error, index) => <p key={index}>{error}</p>));
    }
  };
  // Check for an error during the file upload
  if (isCreateBinaryError) {
    let newErrorMessage = String(createBinaryError);
    if (newErrorMessage.includes("409")) {
      if (operationStatus === OPERATION_WORKFLOW.UPLOADING_FILE) {
        // file already exists
        // ask if user wants to proceed
        setOperationStatus(OPERATION_WORKFLOW.FILE_UPLOADED);
        setOperationStatus(OPERATION_WORKFLOW.CHECKING_FILE_EXISTS);
        setOperationStatus(OPERATION_WORKFLOW.FILE_EXISTS);
      }
    } else {
      if (createBinaryError?.response?.data?.detail) {
        newErrorMessage =
          "Server Error " +
          createBinaryError.response.status +
          ": " +
          JSON.stringify(createBinaryError.response.data.detail);
      }
      if (newErrorMessage !== errorMessage) {
        setErrorMessage(newErrorMessage);
      }
    }
  }

  const onCreateModelVersion = (modelVersionData) => {
    setOperationStatus(OPERATION_WORKFLOW.CREATING_MODEL_VERSION);
    createModelVersion({
      modelId: props.modelId,
      modelVersion: modelVersionData,
    });
  };

  // The file uploaded and there is no error
  if (
    isCreateBinarySuccess &&
    operationStatus === OPERATION_WORKFLOW.UPLOADING_FILE
  ) {
    setOperationStatus(OPERATION_WORKFLOW.FILE_UPLOADED);
    setOperationStatus(OPERATION_WORKFLOW.CHECKING_FILE_EXISTS);
    setOperationStatus(OPERATION_WORKFLOW.FILE_DOESNT_EXIST);
    onCreateModelVersion({
      filename: newBinary.filename,
      storage_id: newBinary.storage_id,
      model_base_image:
        modelBaseImage === "" ? defaultModelBaseImage : modelBaseImage,
      config: buildConfigWithLimits(config, cpuLimit, memoryLimit),
    });
  }

  // Check for an error during the file upload
  if (isCreateModelVersionError) {
    let newErrorMessage = String(createModelVersionError);
    if (createModelVersionError?.response?.data?.detail) {
      newErrorMessage =
        "Server Error " +
        createModelVersionError.response.status +
        ": " +
        JSON.stringify(createModelVersionError.response.data.detail);
    }
    if (newErrorMessage !== errorMessage) {
      setErrorMessage(newErrorMessage);
    }
  }
  // The model version created and there is no error
  if (
    isCreateModelVersionSuccess &&
    operationStatus === OPERATION_WORKFLOW.CREATING_MODEL_VERSION
  ) {
    setOperationStatus(OPERATION_WORKFLOW.MODEL_VERSION_CREATED);
    setOperationStatus(OPERATION_WORKFLOW.COMPLETE);
    closeAndScrollToId(
      `model-${props.modelId}-version-${newModelVersion.version_number}`,
      onClose,
      history,
    );
  }

  const statusRowStyle = {
    minHeight: "30px",
  };

  const loading = (
    <img
      src="/images/NuclearN1-B.gif"
      width="25px"
      height="25px"
      style={{ float: "left", marginLeft: "10px" }}
    />
  );
  const complete = (
    <span
      className="text-success"
      style={{
        fontSize: "135%",
        marginLeft: "10px",
      }}
    >
      <b>✓</b>
    </span>
  );
  const failed = (
    <span
      className="text-danger"
      style={{
        fontSize: "135%",
        marginLeft: "10px",
      }}
    >
      <b>X</b>
    </span>
  );

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      style={{ textAlign: "left" }}
    >
      <CModalHeader>
        <CCol sm="9">
          <h4>Create A New Model Version</h4>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="create-model-version-close-button"
            color="secondary"
            onClick={() => onClose()}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <CModalBody>
        {errorMessage && (
          <CAlert
            color="danger"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {errorMessage}
          </CAlert>
        )}
        <CForm>
          <CFormGroup row>
            <CLabel className="col-sm-4 col-form-label">Model .whl File</CLabel>
            <CCol sm={8}>
              <input
                type="file"
                id="upload-model-version-wheel-input"
                name="model-version-wheel"
                ref={inputFile}
                disabled={operationStatus !== OPERATION_WORKFLOW.IDLE}
                onChange={(e) => {
                  setFile(e.target.files[0]);
                }}
              />
            </CCol>
          </CFormGroup>
          <CFormGroup row>
            <CLabel className="col-sm-4 col-form-label">
              Model Base Image
            </CLabel>
            <CCol sm={8}>
              <CInput
                id="edit-model-base-image-input"
                type="text"
                name="model-base-image"
                placeholder="modelbase:latest"
                value={modelBaseImage}
                onChange={(e) => {
                  setModelBaseImage(e.target.value);
                }}
              />
            </CCol>
          </CFormGroup>
          <CFormGroup row>
            <CLabel className="col-sm-4 col-form-label">
              Model Version Config
            </CLabel>
            <CCol sm={8}>
              <CTextarea
                id="create-new-model-version-config-textarea"
                type="text"
                style={{ fontSize: "85%", fontFamily: "monospace" }}
                spellCheck="false"
                rows="10"
                name="rawdata"
                placeholder="Config (JSON)"
                value={config}
                onChange={(e) => setConfig(e.target.value)}
              />
            </CCol>
          </CFormGroup>
          <CFormGroup row>
            <CLabel className="col-sm-4 col-form-label">
              CPU Limit (cores)
            </CLabel>
            <CCol sm={8}>
              <CInput
                id="create-model-version-cpu-limit-input"
                type="number"
                step="0.5"
                min="1"
                name="cpu-limit"
                placeholder="8 (default)"
                value={cpuLimit}
                onChange={(e) => setCpuLimit(e.target.value)}
              />
            </CCol>
          </CFormGroup>
          <CFormGroup row>
            <CLabel className="col-sm-4 col-form-label">
              Memory Limit (MB)
            </CLabel>
            <CCol sm={8}>
              <CInput
                id="create-model-version-memory-limit-input"
                type="number"
                step="1024"
                min="1"
                name="memory-limit"
                placeholder="24576 (default)"
                value={memoryLimit}
                onChange={(e) => setMemoryLimit(e.target.value)}
              />
            </CCol>
          </CFormGroup>
        </CForm>
      </CModalBody>
      <CModalFooter>
        <CContainer fluid>
          {operationStatus === OPERATION_WORKFLOW.IDLE ? (
            <CRow>
              <CCol
                sm="12"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CButton
                  id="submit-create-model-version-button"
                  color="success"
                  enabled={file}
                  onClick={() => onCreateBinarySubmit()}
                >
                  Create New Model Version
                </CButton>
              </CCol>
            </CRow>
          ) : (
            operationStatus > OPERATION_WORKFLOW.IDLE && (
              <>
                <CRow style={statusRowStyle}>
                  <CCol sm="12">
                    <span style={{ float: "left" }}>
                      <i>Uploading wheel file</i>
                      {operationStatus >= OPERATION_WORKFLOW.FILE_UPLOADED &&
                        complete}
                    </span>

                    {operationStatus === OPERATION_WORKFLOW.UPLOADING_FILE &&
                      loading}
                  </CCol>
                </CRow>
                <CRow style={statusRowStyle}>
                  <CCol sm="12">
                    <span style={{ float: "left" }}>
                      <i>Checking if wheel already exists</i>
                      {operationStatus >=
                        OPERATION_WORKFLOW.FILE_DOESNT_EXIST && complete}
                      {operationStatus ===
                        OPERATION_WORKFLOW.FILE_EXISTS_CANCEL && failed}
                    </span>
                    {operationStatus ===
                      OPERATION_WORKFLOW.CHECKING_FILE_EXISTS ||
                      (operationStatus === OPERATION_WORKFLOW.FILE_EXISTS &&
                        loading)}
                  </CCol>
                </CRow>
                {operationStatus === OPERATION_WORKFLOW.FILE_EXISTS ? (
                  <CRow style={statusRowStyle}>
                    <CCol sm="12">
                      <CAlert color="danger">
                        <p>Wheel file with this name already exists.</p>
                        <p>
                          Proceed with creating new model version using the
                          existing wheel file?
                        </p>
                        <CButtonGroup>
                          <CButton
                            color="success"
                            onClick={() => {
                              // Use the existing wheel file info returned via the 409
                              // error to create a model version with the existing wheel file
                              onCreateModelVersion({
                                filename:
                                  createBinaryError.response.data.detail
                                    .model_binary_info.filename,
                                storage_id:
                                  createBinaryError.response.data.detail
                                    .model_binary_info.storage_id,
                                model_base_image:
                                  modelBaseImage === ""
                                    ? defaultModelBaseImage
                                    : modelBaseImage,
                                config: buildConfigWithLimits(
                                  config,
                                  cpuLimit,
                                  memoryLimit,
                                ),
                              });
                            }}
                          >
                            Yes
                          </CButton>
                          <CButton
                            color="danger"
                            onClick={() => {
                              setOperationStatus(
                                OPERATION_WORKFLOW.FILE_EXISTS_CANCEL,
                              );
                            }}
                          >
                            No
                          </CButton>
                        </CButtonGroup>
                      </CAlert>
                    </CCol>
                  </CRow>
                ) : (
                  operationStatus === OPERATION_WORKFLOW.FILE_EXISTS_CANCEL && (
                    <CRow style={statusRowStyle}>
                      <CCol>
                        <CAlert color="danger">
                          <p>Wheel file with this name already exists.</p>
                          <p> Operation cancelled.</p>
                        </CAlert>
                      </CCol>
                    </CRow>
                  )
                )}
                <CRow style={statusRowStyle}>
                  <CCol sm="12">
                    <span style={{ float: "left" }}>
                      <i>Creating new model version</i>
                      {operationStatus >=
                        OPERATION_WORKFLOW.MODEL_VERSION_CREATED && complete}
                    </span>
                    {operationStatus ===
                      OPERATION_WORKFLOW.CREATING_MODEL_VERSION && loading}
                  </CCol>
                </CRow>
                <CRow style={statusRowStyle}>
                  <CCol sm="12">
                    <span style={{ float: "left" }}>
                      <i>
                        Model Version{" "}
                        {newModelVersion?.version_number
                          ? newModelVersion?.version_number
                          : "?"}{" "}
                        created
                      </i>
                      {operationStatus === OPERATION_WORKFLOW.COMPLETE &&
                        complete}
                    </span>
                  </CCol>
                </CRow>
              </>
            )
          )}
        </CContainer>
      </CModalFooter>
    </CModal>
  );
};

CreateModelVersionModal.propTypes = {
  modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export const ModifyModelVersionModal = (props) => {
  const [config, setConfig] = useState("");
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [modelBaseImage, setModelBaseImage] = useState("");
  const [successModify, setSuccessModify] = useState(false);
  const [cpuLimit, setCpuLimit] = useState("");
  const [memoryLimit, setMemoryLimit] = useState("");

  const { data: modelVersionData, isSuccess: getModelVersionSuccess } =
    useGetModelVersion(props.modelId, props.modelVersionId);

  const { mutate: patchModelVersion, isSuccess: patchModelVersionSuccess } =
    usePatchModelVersion();

  const onClose = () => {
    props.onClose();
  };

  const onSubmit = (
    modelId,
    modelVersionId,
    configData,
    modelBaseImageData,
    cpuLimitData,
    memoryLimitData,
  ) => {
    let errors = validateConfig(configData);
    errors = errors.concat(
      validateResourceLimits(cpuLimitData, memoryLimitData),
    );
    if (errors.length === 0) {
      setErrorMessage(null);
      patchModelVersion({
        modelId: modelId,
        modelVersion: modelVersionId,
        modelVersionData: {
          config: buildConfigWithLimits(
            configData,
            cpuLimitData,
            memoryLimitData,
          ),
          model_base_image: modelBaseImageData,
        },
      });
    } else {
      setErrorMessage(errors.map((error, index) => <p key={index}>{error}</p>));
    }
  };

  useEffect(() => {
    if (modelVersionData && getModelVersionSuccess) {
      if (!isObjEmpty(modelVersionData.config)) {
        // Extract cpu_limit and memory_limit from config
        const configObj = modelVersionData.config;
        if (configObj.cpu_limit !== undefined) {
          setCpuLimit(String(configObj.cpu_limit));
        } else {
          setCpuLimit("");
        }
        if (configObj.memory_limit !== undefined) {
          setMemoryLimit(String(configObj.memory_limit));
        } else {
          setMemoryLimit("");
        }
        // Create config without cpu_limit and memory_limit for the textarea
        const otherConfig = { ...configObj };
        delete otherConfig.cpu_limit;
        delete otherConfig.memory_limit;
        if (!isObjEmpty(otherConfig)) {
          setConfig(JSON.stringify(otherConfig));
        } else {
          setConfig("");
        }
      } else {
        // No config present; reset related fields
        setCpuLimit("");
        setMemoryLimit("");
        setConfig("");
      }
      setModelBaseImage(modelVersionData.model_base_image);
    }
  }, [getModelVersionSuccess]);

  useEffect(() => {
    if (patchModelVersionSuccess) {
      setSuccessModify(true);
    }
  }, [patchModelVersionSuccess]);

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      style={{ textAlign: "left" }}
    >
      <CModalHeader>
        <CCol sm="9">
          <h4>Modify A Model Version</h4>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="create-model-version-close-button"
            color="secondary"
            onClick={() => onClose()}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      {successModify ? (
        <div style={{ padding: "15px" }}>
          Model version successfully modified{" "}
        </div>
      ) : (
        <>
          <CModalBody>
            {errorMessage && (
              <CAlert
                color="danger"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {errorMessage}
              </CAlert>
            )}
            <CForm>
              <CFormGroup row>
                <CLabel className="col-sm-4 col-form-label">
                  Model Base Image
                </CLabel>
                <CCol sm={8}>
                  <CInput
                    id="edit-model-base-image-input"
                    type="text"
                    name="model-base-image"
                    placeholder="modelbase:latest"
                    value={modelBaseImage}
                    onChange={(e) => {
                      setModelBaseImage(e.target.value);
                    }}
                  />
                </CCol>
              </CFormGroup>
              <CFormGroup row>
                <CLabel className="col-sm-4 col-form-label">
                  Model Version Config
                </CLabel>
                <CCol sm={8}>
                  <CTextarea
                    id="create-new-model-version-config-textarea"
                    type="text"
                    style={{ fontSize: "85%", fontFamily: "monospace" }}
                    spellCheck="false"
                    rows="10"
                    name="rawdata"
                    placeholder="Config (JSON)"
                    value={config}
                    onChange={(e) => setConfig(e.target.value)}
                  />
                </CCol>
              </CFormGroup>
              <CFormGroup row>
                <CLabel className="col-sm-4 col-form-label">
                  CPU Limit (cores)
                </CLabel>
                <CCol sm={8}>
                  <CInput
                    id="modify-model-version-cpu-limit-input"
                    type="number"
                    step="0.5"
                    min="1"
                    name="cpu-limit"
                    placeholder="8 (default)"
                    value={cpuLimit}
                    onChange={(e) => setCpuLimit(e.target.value)}
                  />
                </CCol>
              </CFormGroup>
              <CFormGroup row>
                <CLabel className="col-sm-4 col-form-label">
                  Memory Limit (MB)
                </CLabel>
                <CCol sm={8}>
                  <CInput
                    id="modify-model-version-memory-limit-input"
                    type="number"
                    step="1024"
                    min="1"
                    name="memory-limit"
                    placeholder="24576 (default)"
                    value={memoryLimit}
                    onChange={(e) => setMemoryLimit(e.target.value)}
                  />
                </CCol>
              </CFormGroup>
            </CForm>
          </CModalBody>
          <CModalFooter>
            <CContainer fluid>
              <CRow>
                <CCol
                  sm="12"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CButton
                    id="submit-create-model-version-button"
                    color="success"
                    onClick={() =>
                      onSubmit(
                        props.modelId,
                        props.modelVersionId,
                        config,
                        modelBaseImage,
                        cpuLimit,
                        memoryLimit,
                      )
                    }
                  >
                    Modify Model Version
                  </CButton>
                </CCol>
              </CRow>
            </CContainer>
          </CModalFooter>
        </>
      )}
    </CModal>
  );
};

ModifyModelVersionModal.propTypes = {
  modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  modelVersionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  config: PropTypes.any,
  modelBaseImage: PropTypes.string,
};
